import { createPortal } from "react-dom"
import { ChevronDown, Check } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { controlBase, onFocus, onBlur } from "@/app/lib/tableStyles"
import { usePopover } from "@/app/hooks/usePopover"

export type SelectOption = string | { label: string; value: string }

interface SelectMenuProps {
  value: string
  onChange: (v: string) => void
  options: SelectOption[]
  /**
   * When set, a clearable top row (value "") is rendered with this label and the
   * trigger shows it whenever value is empty — the single-select "All" / unset
   * state used by filters. Omit for required form fields that always hold a value.
   */
  placeholder?: string
  width?: string
  /** Trigger background; defaults to the filter-bar surface. Forms pass bgSurface2. */
  background?: string
  theme: ClientTheme
}

const norm = (o: SelectOption) => (typeof o === "string" ? { label: o, value: o } : o)

/**
 * Themed single-select dropdown matching MultiSelectFilter's look (trigger +
 * popover of rows with a check on the active option). Shared by the filter bars
 * (via SelectFilter) and form fields, so all dropdowns read identically.
 */
export function SelectMenu({
  value,
  onChange,
  options,
  placeholder,
  width = "180px",
  background,
  theme,
}: SelectMenuProps) {
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLDivElement>()
  const ctrlBase = controlBase(theme)

  const opts = options.map(norm)
  const isEmpty = value === ""
  const current = opts.find((o) => o.value === value)
  const label = isEmpty ? (placeholder ?? "") : (current?.label ?? value)

  const pick = (v: string) => {
    onChange(v)
    setOpen(false)
  }

  const row = (rowLabel: string, rowValue: string, active: boolean) => (
    <button
      key={rowValue || "__empty__"}
      type="button"
      onClick={() => pick(rowValue)}
      className="flex items-center justify-between gap-2 w-full px-3 py-2"
      style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.bgSurface2
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent"
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "12px",
          color: active ? theme.brandPrimary : theme.textPrimary,
          fontWeight: active ? 500 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {rowLabel}
      </span>
      {active && <Check size={12} style={{ color: theme.brandPrimary, flexShrink: 0 }} />}
    </button>
  )

  return (
    <div ref={triggerRef} className="relative flex-shrink-0" style={{ width }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={onFocus(theme)}
        onBlur={onBlur(theme)}
        style={{
          ...ctrlBase,
          ...(background ? { backgroundColor: background } : {}),
          width: "100%",
          display: "flex",
          alignItems: "center",
          paddingLeft: "10px",
          paddingRight: "28px",
          cursor: "pointer",
          textAlign: "left",
          color: isEmpty ? theme.textMuted : theme.textPrimary,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </button>
      <ChevronDown
        size={12}
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          color: theme.textDim,
          pointerEvents: "none",
        }}
      />

      {/* Portaled to <body> so the card's overflow-hidden can't clip it. */}
      {open &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            className="rounded-lg overflow-hidden"
            style={{
              ...popoverStyle,
              zIndex: 60,
              overflowY: "auto",
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {placeholder !== undefined && row(placeholder, "", isEmpty)}
            {opts.length === 0 ? (
              <div
                className="px-3 py-3"
                style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}
              >
                No options
              </div>
            ) : (
              opts.map((o) => row(o.label, o.value, o.value === value))
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
